/* Default Peikko Designer(r) DELTABEAM SELECT theme */

import { path } from 'ramda'

interface BasicColors {
  color: string
  background: string
}

interface ColorPalet {
  default: string
  light: string
  inactive: string
  invert: string
  checked: string
  head: string
  modalHead: string
  brand: string
  error: string
}

interface BasicFont {
  fontSize: string
}

interface ButtonExtras {
  hover: string
}

export type ButtonProps = BasicColors & ButtonExtras & BasicFont

export interface SelectTheme {
  colors: ColorPalet
  hoverColors: ColorPalet
  fonts: {
    titleSize: string
    largeSize: string
    defaultSize: string
    smallSize: string
    tinySize: string
  }
  home: {
    h2Size: string
    h3Size: string
  }
  header: {
    height: string
  } & BasicColors &
    BasicFont
  toolbar: {
    buttonFontSize: string
  } & BasicColors &
    BasicFont
  sidebar: {
    title: BasicColors & BasicFont
    header: BasicColors & BasicFont
    body: BasicColors &
      BasicFont & {
        altBackground: string
        headerSize: string
      }
  }
  projectTree: {
    accordion: {
      headerBackground: string
      activeBackground: string
    }
    elements: {
      elementButtonColor: string
      elementButtonBackground: string
      activeButtonBorder: string
      inactiveButtonBorder: string
    } & BasicFont
  }
  workspace: {
    tabs: BasicColors & {
      height: string
      tabBarBg: string
    }
    arrows: {
      dimension: string
      coords: {
        x: string
        y: string
        z: string
      }
    }
  }
  feature: {
    background: string
  }
  previz: {
    background: string
  }
  infoBar: BasicColors
  uiElements: {
    button: ButtonProps
    primaryButton: ButtonProps
    tooltip: BasicColors
  }
  checked: string
  project: {
    button: {
      gray: string
      hover: string
    } & BasicColors
    noProjects: {
      background: string
      border: string
    }
  }
  modal: {
    header: {
      backgroundHover: string
    } & BasicColors
    alert: {
      topBar: string
    }
  }
  feedback: {
    h2Size: string
    h3Size: string
    h5Size: string
    largeSize: string
    title: BasicColors
    firstPage: {} & BasicColors & BasicFont
  } & BasicColors &
    BasicFont
}

export const PeikkoGreen = '#96C11E'
export const PeikkoGreenHighlight = '#669900'

// Color palets
const colors = {
  default: '#333f48',
  light: '#7c878e',
  inactive: '#d6d9da',
  invert: '#ffffff',
  head: '#b0b7bb',
  modalHead: '#00b0b9',
  checked: '#0072ce',
  brand: PeikkoGreen,
  error: '#f478af'
}

const hoverColors = {
  default: '#4a5b68',
  light: '#a0a7ac',
  inactive: '#afb4b6',
  invert: 'f2f2f2',
  head: '#929ba0',
  modalHead: '#99DFE3',
  checked: '#008cff',
  brand: PeikkoGreenHighlight,
  error: '#f04290'
}

const tabHeight = '30px'

const themeObj: SelectTheme = {
  colors,
  hoverColors,
  fonts: {
    titleSize: '24px',
    largeSize: '16px',
    defaultSize: '14px',
    smallSize: '13px',
    tinySize: '10px'
  },
  home: {
    h2Size: '56px',
    h3Size: '28px'
  },
  header: {
    height: '72px',
    background: 'white',
    color: colors.default,
    fontSize: '14px'
  },
  toolbar: {
    color: colors.invert,
    background: colors.default,
    fontSize: '14px',
    buttonFontSize: '13px'
  },
  sidebar: {
    title: {
      color: colors.invert,
      background: colors.light,
      fontSize: '14px'
    },
    header: {
      color: colors.light,
      background: colors.inactive,
      fontSize: '13px'
    },
    body: {
      color: colors.default,
      background: colors.invert,
      altBackground: '#EEF1F2',
      headerSize: '14px',
      fontSize: '13px'
    }
  },
  projectTree: {
    accordion: {
      headerBackground: '#e3e6e5',
      activeBackground: '#f4f5f5'
    },
    elements: {
      elementButtonColor: 'black',
      elementButtonBackground: colors.inactive,
      activeButtonBorder: `2px solid ${colors.brand}`,
      inactiveButtonBorder: `2px solid transparent`,
      fontSize: '14px'
    }
  },
  workspace: {
    tabs: {
      background: '#E9EBEC',
      color: '#B7BDC1',
      height: tabHeight,
      tabBarBg: '#b0b7bb'
    },
    arrows: {
      dimension: '#ffff00',
      coords: {
        x: 'red',
        y: '#14DC14',
        z: 'blue'
      }
    }
  },
  feature: {
    background: '#E8EBEC'
  },
  previz: {
    background: 'linear-gradient(to top, #999 0%,  #e3e6e5 100%);'
  },
  infoBar: {
    color: colors.invert,
    background: `linear-gradient( 90deg, ${colors.modalHead} 0%, ${
      hoverColors.modalHead
    } 100%)`
  },
  uiElements: {
    button: {
      color: colors.invert,
      background: ' #8A999F',
      hover: '#d8dde0',
      fontSize: '14px'
    },
    primaryButton: {
      color: colors.invert,
      background: colors.brand,
      hover: PeikkoGreenHighlight,
      fontSize: '14px'
    },
    tooltip: {
      color: 'white',
      background: colors.modalHead
    }
  },
  checked: colors.checked,
  project: {
    button: {
      color: colors.invert,
      background: colors.head,
      hover: hoverColors.head,
      gray: '#7B878E'
    },
    noProjects: {
      background: '#E9EBEC',
      border: '#C0C5C8'
    }
  },
  modal: {
    header: {
      color: colors.invert,
      background: colors.modalHead,
      backgroundHover: hoverColors.modalHead
    },
    alert: {
      topBar: colors.modalHead
    }
  },
  feedback: {
    color: colors.default,
    background: colors.invert,
    fontSize: '14px',
    h2Size: '32px',
    h3Size: '20px',
    h5Size: '18px',
    largeSize: '16px',
    title: {
      color: colors.invert,
      background: colors.modalHead
    },
    firstPage: {
      color: colors.invert,
      background: colors.modalHead,
      fontSize: '14px'
    }
  }
}

type Themed = {
  theme: SelectTheme
}
export const sProp = (sPath: string[]) => (prop: string) => (props: Themed) =>
  path(sPath, props.theme)[prop]

export default themeObj



// WEBPACK FOOTER //
// ./src/theme/defaultTheme.ts